import { useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SubActions, updateFailedToConnectToLiveUpdates } from '../redux-store/thunks/subscriptions.thunks';
import { enableLiveUpdates } from '../redux-store/thunks/global.thunks';

import {
  useDatasetsLiveUpdates,
  usePipesLiveUpdates,
  useSystemsLiveUpdates,
} from './useLiveUpdates';

import { getWebSocketFactory } from '../network/websocketFactory';
import { routeMessage } from '../network/messageRouter';
import { getConnectionString } from '../utils/subscription.utils';
import { getWebSocketUrlFromApiUrl } from '../utils/common.utils';

import { useIsLiveUpdatesEnabled } from './useIsLiveUpdatesEnabled.hook';

import type {
  DatasetsData,
  PipesData,
  SocketIOInstance,
  SystemsData,
} from '../types/network.types';
import type { RootState } from '../types/state.types';

type SocketIO = {
  current: SocketIOInstance | null;
};

const socket: SocketIO = { current: null };

export const useSocketIO = (subscriptionId?: string) => {
  const dispatch = useDispatch();

  const isLiveUpdatesEnabled = useIsLiveUpdatesEnabled(subscriptionId ?? '');

  const jwt = useSelector((state: RootState) => state.subscription.token);
  const url = useSelector((state: RootState) => state.subscription.url);

  const connect = () => {
    const persistedURL = getConnectionString(subscriptionId || '');
    const wsUrl = getWebSocketUrlFromApiUrl(persistedURL ?? url);

    const socketFactory = getWebSocketFactory();
    socket.current = socketFactory({
      jwt,
      wsUrl,
    });

    usePipesLiveUpdates(socket.current);
    useSystemsLiveUpdates(socket.current);
    useDatasetsLiveUpdates(socket.current);

    if (!socket.current) {
      console.warn('WebSocket not supported in this browser');
      dispatch(SubActions.updateSupportWebSockets(false));
      return;
    }

    // this event is fired by the Socket instance upon connection and reconnection.
    socket.current.on('connect', () => {
      socket.current?.emit('get_pipes', (data: PipesData) => {
        const pipes = data.data ?? {};
        const incomingPipes = Object.values(pipes);

        if (pipes) {
          routeMessage({ type: 'pipes', data: incomingPipes });
        }
      });

      socket.current?.emit('get_systems', (data: SystemsData) => {
        const systems = data.data ?? {};
        const incomingSystems = Object.values(systems);

        if (systems) {
          routeMessage({ type: 'systems', data: incomingSystems });
        }
      });

      socket.current?.emit('get_datasets', (data: DatasetsData) => {
        const systems = data.data ?? {};
        const incomingSystems = Object.values(systems);

        if (systems) {
          routeMessage({ type: 'datasets', data: incomingSystems });
        }
      });

      socket.current?.emit('subscribe_pipes');
      socket.current?.emit('subscribe_systems');
      socket.current?.emit('subscribe_datasets');
    });

    // this event is fired upon connection failure.
    socket.current.on('connect_error', (error) => {
      // server messages from ConnectionRefusedError: "token expired" | "invalid token" | ...
      if (error.message === 'xhr poll error' || error.message === 'websocket error') {
        dispatch(updateFailedToConnectToLiveUpdates(true));
        socket.current?.close();
      }

      if (error.message === 'token expired') {
        // TODO:
        // 1) use your refresh-token flow to get a new access token
        // 2) reconnect with the new token:
        // process.env.JWT = "<NEW_JWT>";
        // socket.current.auth = { token: process.env.JWT };
        // socket.current?.disconnect().connect();
      }

      if (socket.current?.active) {
        // temporary failure, the socket will automatically try to reconnect
      } else {
        // the connection was denied by the server
        // in that case, `socket.current.connect()` must be manually called in order to reconnect
        console.info(error.message);
      }
    });

    // this event is fired upon disconnection.
    socket.current.on('disconnect', (reason, details) => {
      console.info('socket disconnected', reason, details);
    });
  };

  const closeConnection = () => {
    socket.current?.close();
    dispatch(enableLiveUpdates());
  };

  useLayoutEffect(() => {
    if (!jwt) {
      console.warn('No JWT provided for WebSocket connection');
      return;
    }

    if (isLiveUpdatesEnabled === true && !socket.current?.connected && isLiveUpdatesEnabled) {
      connect();
    }

    if (isLiveUpdatesEnabled === false) {
      socket.current?.close();
    }
  }, [subscriptionId, jwt, url, isLiveUpdatesEnabled]);

  return { closeConnection };
};

export const useCloseSocketIOConnection = (subscriptionId: string, closeConnection: () => void) => {
  useEffect(() => () => closeConnection(), [subscriptionId]);
};
